<script>
import { formatMinskDate } from '@/scripts/tools.js';
import constants from '@/constants';
import PaginationController from '@/components/base-components/PaginationController.vue';
import MaterialRequestStatusBadge from '@/components/page-publisher-material-requests/MaterialRequestStatusBadge.vue';
import MaterialRequestsAdminFilters from '@/components/page-publisher-material-requests/MaterialRequestsAdminFilters.vue';
import MaterialRequestStatusEditor from '@/components/page-publisher-material-requests/MaterialRequestStatusEditor.vue';

export default {
    name: 'MaterialRequestsAdminList',

    components: {
        PaginationController,
        MaterialRequestStatusBadge,
        MaterialRequestsAdminFilters,
        MaterialRequestStatusEditor,
    },

    data() {
        return {
            // Local map: requestId → editable admin notes draft
            notesEditMap: {},
            // Which request is currently being edited in the status editor modal
            editingRequest: null,
            // Track per-file download loading state: key = `${requestId}-${fileId}`
            downloadingFileKey: null,
            // Expanded rows map: requestId → boolean
            expandedRows: {},
        };
    },

    computed: {
        requests() {
            return this.$store.getters.ADMIN_MATERIAL_REQUESTS;
        },
        isLoading() {
            return this.$store.getters.ADMIN_MATERIAL_REQUESTS_LOADING;
        },
        isBadRequest() {
            return this.$store.getters.ADMIN_MATERIAL_REQUESTS_BAD_REQUEST;
        },
        totalItems() {
            return this.$store.getters.ADMIN_MATERIAL_REQUESTS_TOTAL;
        },

        // PaginationController uses 0-indexed currentPage and maxPage
        currentPageIndex() {
            return (this.$store.getters.ADMIN_MATERIAL_REQUESTS_PAGE || 1) - 1;
        },
        maxPageIndex() {
            return (
                (this.$store.getters.ADMIN_MATERIAL_REQUESTS_TOTAL_PAGES || 1) -
                1
            );
        },

        perPage() {
            return this.$store.getters.ADMIN_MATERIAL_REQUESTS_PER_PAGE;
        },

        displayFrom() {
            if (this.totalItems === 0) return 0;
            return this.currentPageIndex * this.perPage + 1;
        },
        displayTo() {
            return Math.min(
                (this.currentPageIndex + 1) * this.perPage,
                this.totalItems
            );
        },
    },

    watch: {
        requests: {
            immediate: true,
            handler(newRequests) {
                // Sync notes edit map: keep any in-progress local edits,
                // populate new entries from store data
                const updated = {};
                newRequests.forEach((r) => {
                    updated[r.id] = Object.prototype.hasOwnProperty.call(
                        this.notesEditMap,
                        r.id
                    )
                        ? this.notesEditMap[r.id]
                        : r.admin_notes || '';
                });
                this.notesEditMap = updated;
            },
        },
    },

    methods: {
        // ── Date formatting ──────────────────────────────────────────────────
        formatDate(isoString) {
            return formatMinskDate(isoString, true);
        },

        // ── Type display ─────────────────────────────────────────────────────
        formatType(type) {
            return type === constants.MATERIAL_REQUEST_TYPE.ARCHIVE
                ? 'Archive'
                : type === constants.MATERIAL_REQUEST_TYPE.LINK
                  ? 'Link'
                  : type || '—';
        },

        // ── Admin notes inline editing ───────────────────────────────────────
        onNotesInput(requestId, event) {
            this.notesEditMap = {
                ...this.notesEditMap,
                [requestId]: event.target.value,
            };
        },

        async onNotesBlur(request) {
            const draft = this.notesEditMap[request.id] ?? '';
            const original = request.admin_notes || '';
            if (draft === original) return; // Nothing changed

            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[MaterialRequestsAdminList.onNotesBlur] Saving notes for request',
                    request.id
                    // Note content intentionally not logged
                );
            }

            try {
                await this.$store.dispatch('updateAdminMaterialRequestStatus', {
                    requestId: request.id,
                    status: request.status,
                    adminNotes: draft,
                });
            } catch {
                // Restore original on error
                this.notesEditMap = {
                    ...this.notesEditMap,
                    [request.id]: original,
                };
            }
        },

        // ── Status editor ────────────────────────────────────────────────────
        openStatusEditor(request) {
            if (process.env.NODE_ENV !== 'production') {
                console.info(
                    '[MaterialRequestsAdminList.openStatusEditor] Opening for request',
                    request.id
                );
            }
            this.editingRequest = request;
        },

        closeStatusEditor() {
            this.editingRequest = null;
        },

        // ── Pagination ───────────────────────────────────────────────────────
        async handleFirst() {
            this.$store.commit('SET_ADMIN_MATERIAL_REQUESTS_PAGE', 1);
            await this.$store.dispatch('loadAdminMaterialRequests');
        },
        async handleBack() {
            const page = this.$store.getters.ADMIN_MATERIAL_REQUESTS_PAGE;
            if (page <= 1) return;
            this.$store.commit('SET_ADMIN_MATERIAL_REQUESTS_PAGE', page - 1);
            await this.$store.dispatch('loadAdminMaterialRequests');
        },
        async handleNext() {
            const page = this.$store.getters.ADMIN_MATERIAL_REQUESTS_PAGE;
            const total = this.$store.getters.ADMIN_MATERIAL_REQUESTS_TOTAL_PAGES;
            if (page >= total) return;
            this.$store.commit('SET_ADMIN_MATERIAL_REQUESTS_PAGE', page + 1);
            await this.$store.dispatch('loadAdminMaterialRequests');
        },
        async handleLast() {
            const total = this.$store.getters.ADMIN_MATERIAL_REQUESTS_TOTAL_PAGES;
            this.$store.commit('SET_ADMIN_MATERIAL_REQUESTS_PAGE', total);
            await this.$store.dispatch('loadAdminMaterialRequests');
        },
        async changePerPage(event) {
            const value = parseInt(event.target.value, 10);
            this.$store.commit('SET_ADMIN_MATERIAL_REQUESTS_PER_PAGE', value);
            this.$store.commit('SET_ADMIN_MATERIAL_REQUESTS_PAGE', 1);
            await this.$store.dispatch('loadAdminMaterialRequests');
        },

        // ── Row expand/collapse ──────────────────────────────────────────────
        toggleExpand(requestId) {
            this.expandedRows = {
                ...this.expandedRows,
                [requestId]: !this.expandedRows[requestId],
            };
        },

        // ── Archive file download ────────────────────────────────────────────
        async downloadFile(requestId, fileId) {
            const key = `${requestId}-${fileId}`;
            if (this.downloadingFileKey === key) return;
            this.downloadingFileKey = key;
            try {
                await this.$store.dispatch('downloadAdminMaterialRequestFile', {
                    requestId,
                    fileId,
                });
            } finally {
                this.downloadingFileKey = null;
            }
        },
    },
};
</script>

<template>
    <div class="list_container">
        <!-- Filters -->
        <MaterialRequestsAdminFilters />

        <!-- Loading state -->
        <div v-if="isLoading" class="state_block state_loading">
            <p>Loading material requests...</p>
        </div>

        <!-- Error state -->
        <div v-else-if="isBadRequest" class="state_block state_error">
            <p>
                Failed to load material requests. Please try again.
            </p>
            <button
                class="btn_retry"
                @click="$store.dispatch('loadAdminMaterialRequests')"
            >
                Retry
            </button>
        </div>

        <!-- Empty state -->
        <div
            v-else-if="requests.length === 0"
            class="state_block state_empty"
        >
            <p>No material requests found.</p>
        </div>

        <!-- Table -->
        <template v-else>
            <div class="table_scroll_container">
                <table class="requests_table">
                    <thead>
                        <tr>
                            <th class="th_140">Publisher</th>
                            <th class="th_140">Admin</th>
                            <th class="th_80 th_center">Type</th>
                            <th class="th_80 th_center">Items</th>
                            <th class="th_110 th_center">Status</th>
                            <th class="th_120">Created</th>
                            <th class="th_40"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <template
                            v-for="request in requests"
                            :key="request.id"
                        >
                            <!-- Main row — click to expand -->
                            <tr
                                class="request_row"
                                :class="{ request_row_expanded: expandedRows[request.id] }"
                                @click="toggleExpand(request.id)"
                            >
                                <!-- Publisher -->
                                <td class="cell_publisher">
                                    <div class="publisher_name">
                                        {{ request.requester_name || '—' }}
                                    </div>
                                    <div
                                        v-if="request.requester_email"
                                        class="publisher_email"
                                    >
                                        {{ request.requester_email }}
                                    </div>
                                </td>

                                <!-- Admin -->
                                <td class="cell_publisher">
                                    <div class="publisher_name">
                                        {{ request.assigned_admin_name || '—' }}
                                    </div>
                                    <div
                                        v-if="request.assigned_admin_email"
                                        class="publisher_email"
                                    >
                                        {{ request.assigned_admin_email }}
                                    </div>
                                </td>

                                <!-- Type -->
                                <td class="cell_center">
                                    <span class="type_badge" :class="`type_` + request.request_type">
                                        {{ formatType(request.request_type) }}
                                    </span>
                                </td>

                                <!-- Items count -->
                                <td class="cell_center">
                                    <span class="items_count">
                                        {{ request.request_type === 'archive'
                                            ? (request.files ? request.files.length : 0)
                                            : (request.payload_urls ? request.payload_urls.length : 0) }}
                                    </span>
                                </td>

                                <!-- Status -->
                                <td class="cell_center">
                                    <MaterialRequestStatusBadge
                                        :status="request.status"
                                    />
                                </td>

                                <!-- Created date -->
                                <td class="cell_date">
                                    {{ formatDate(request.created_at) }}
                                </td>

                                <!-- Chevron -->
                                <td class="cell_chevron">
                                    <svg
                                        class="chevron_icon"
                                        :class="{ chevron_open: expandedRows[request.id] }"
                                        width="16" height="16" viewBox="0 0 16 16"
                                        fill="none" xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </td>
                            </tr>

                            <!-- Expanded detail row -->
                            <tr
                                v-if="expandedRows[request.id]"
                                :key="`${request.id}-expand`"
                                class="expand_row"
                            >
                                <td colspan="7" class="expand_cell">
                                    <div class="expand_body">

                                        <!-- Archive files list -->
                                        <div
                                            v-if="request.request_type === 'archive' && request.files && request.files.length"
                                            class="expand_section"
                                        >
                                            <div class="expand_section_label">Files</div>
                                            <div class="file_list">
                                                <div
                                                    v-for="file in request.files"
                                                    :key="file.id"
                                                    class="file_item"
                                                >
                                                    <span class="file_name">{{ file.filename }}</span>
                                                    <button
                                                        class="btn_download"
                                                        :disabled="downloadingFileKey === `${request.id}-${file.id}`"
                                                        @click.stop="downloadFile(request.id, file.id)"
                                                    >
                                                        {{ downloadingFileKey === `${request.id}-${file.id}` ? '…' : 'Download' }}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Link URLs list -->
                                        <div
                                            v-if="request.request_type !== 'archive' && request.payload_urls && request.payload_urls.length"
                                            class="expand_section"
                                        >
                                            <div class="expand_section_label">Links</div>
                                            <div class="url_list">
                                                <div
                                                    v-for="(url, i) in request.payload_urls"
                                                    :key="i"
                                                    class="url_item"
                                                >
                                                    <a
                                                        :href="url"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        class="url_link"
                                                        @click.stop
                                                    >{{ url }}</a>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Publisher comment -->
                                        <div
                                            v-if="request.comment"
                                            class="expand_section"
                                        >
                                            <div class="expand_section_label">Publisher Comment</div>
                                            <div class="expand_text">{{ request.comment }}</div>
                                        </div>

                                        <!-- Admin notes editable -->
                                        <div class="expand_section">
                                            <div class="expand_section_label">Admin Notes</div>
                                            <textarea
                                                class="notes_textarea"
                                                :value="notesEditMap[request.id] ?? ''"
                                                @input="onNotesInput(request.id, $event)"
                                                @blur="onNotesBlur(request)"
                                                @click.stop
                                                rows="2"
                                                placeholder="Add admin notes..."
                                            />
                                        </div>

                                        <!-- Actions -->
                                        <div class="expand_actions">
                                            <button
                                                class="btn_change_status"
                                                @click.stop="openStatusEditor(request)"
                                            >
                                                Change Status
                                            </button>
                                        </div>

                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div class="pagination_controls">
                <div class="show_info">
                    <span v-if="totalItems > 0">
                        Showing {{ displayFrom }}–{{ displayTo }} of
                        {{ totalItems }}
                    </span>
                </div>

                <div class="pagination_buttons">
                    <PaginationController
                        :currentPage="currentPageIndex"
                        :maxPage="maxPageIndex"
                        @first="handleFirst"
                        @back="handleBack"
                        @next="handleNext"
                        @last="handleLast"
                    />
                </div>

                <div class="per_page_selector">
                    <label>Per page:</label>
                    <select :value="perPage" @change="changePerPage">
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                </div>
            </div>
        </template>

        <!-- Status editor modal -->
        <MaterialRequestStatusEditor
            v-if="editingRequest"
            :request="editingRequest"
            @close="closeStatusEditor"
        />
    </div>
</template>

<style scoped>
.list_container {
    position: relative;
}

/* State blocks */
.state_block {
    padding: 40px 20px;
    text-align: center;
    color: var(--text-color-secondary);
    font-size: 15px;
}

.state_error {
    color: var(--text-color);
}

.btn_retry {
    margin-top: 12px;
    padding: 8px 20px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: transparent;
    color: var(--text-color);
    cursor: pointer;
    font-size: 14px;
}

.btn_retry:hover {
    border-color: var(--primary-color);
}

/* Table */
.table_scroll_container {
    overflow-x: auto;
    margin-bottom: 12px;
}

.requests_table {
    width: 100%;
    min-width: 700px;
    border-collapse: collapse;
}

.requests_table th {
    padding: 10px 12px;
    text-align: left;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: var(--text-color-secondary);
    background: var(--background-secondary);
    border-bottom: 2px solid var(--border-color);
    white-space: nowrap;
}

.requests_table td {
    padding: 10px 12px;
    border-bottom: 1px solid var(--border-color);
    color: var(--text-color);
    vertical-align: top;
    font-size: 13px;
}

.request_row {
    cursor: pointer;
}

.request_row:hover td {
    background: var(--background-hover);
}

.request_row_expanded td {
    background: var(--background-secondary);
}

/* Chevron */
.chevron_icon {
    color: var(--text-color-secondary);
    transition: transform 0.2s;
    display: block;
    margin: 0 auto;
}

.chevron_open {
    transform: rotate(180deg);
}

/* Items count */
.items_count {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-color-secondary);
}

/* Expanded row */
.expand_row td {
    background: var(--background-secondary);
    padding: 0;
}

.expand_cell {
    padding: 0 !important;
    border-bottom: 2px solid var(--border-color) !important;
}

.expand_body {
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.expand_section {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.expand_section_label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: var(--text-color-secondary);
}

.expand_text {
    font-size: 13px;
    color: var(--text-color);
    white-space: pre-wrap;
    word-break: break-word;
}

.expand_actions {
    display: flex;
    gap: 8px;
    padding-top: 4px;
}

/* URL list */
.url_list {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.url_item {
    display: flex;
    align-items: center;
}


/* Column widths */
.th_40 { width: 40px; }
.th_80 { width: 80px; }
.th_110 { width: 110px; }
.th_120 { width: 120px; }
.th_140 { width: 140px; }
.th_center { text-align: center; }
.cell_center { text-align: center; }
.cell_chevron { text-align: center; width: 40px; }

/* Publisher cell */
.publisher_name {
    font-weight: 600;
    font-size: 13px;
}

.publisher_email {
    font-size: 11px;
    color: var(--text-color-secondary);
    margin-top: 2px;
    font-family: monospace;
}

/* Type badge */
.type_badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.type_archive {
    background: #e9d5ff;
    color: #6b21a8;
}

.type_link {
    background: #bfdbfe;
    color: #1e40af;
}

/* URL link */
.url_link {
    color: var(--primary-color, #5b73e8);
    text-decoration: none;
    font-size: 12px;
}

.url_link:hover {
    text-decoration: underline;
}

/* Admin notes textarea */
.notes_textarea {
    width: 100%;
    min-width: 160px;
    padding: 6px 8px;
    border: 1px solid transparent;
    border-radius: 4px;
    background: transparent;
    color: var(--text-color);
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    transition: border-color 0.2s, background 0.2s;
    box-sizing: border-box;
}

.notes_textarea:hover {
    border-color: var(--border-color);
    background: var(--background-secondary);
}

.notes_textarea:focus {
    outline: none;
    border-color: var(--primary-color);
    background: var(--background-color);
}

/* Date cell */
.cell_date {
    white-space: nowrap;
    font-size: 12px;
    color: var(--text-color-secondary);
}

/* Change status button */
.btn_change_status {
    padding: 5px 10px;
    border: 1px solid var(--border-color);
    border-radius: 5px;
    background: transparent;
    color: var(--text-color);
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s;
}

.btn_change_status:hover {
    background: var(--background-secondary);
    border-color: var(--primary-color, #5b73e8);
    color: var(--primary-color, #5b73e8);
}

/* Archive file list */
.file_list {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.file_item {
    display: flex;
    align-items: center;
    gap: 6px;
}

.file_name {
    font-size: 12px;
    word-break: break-all;
    flex: 1;
}

.btn_download {
    padding: 2px 8px;
    border: 1px solid var(--primary-color, #5b73e8);
    border-radius: 4px;
    background: transparent;
    color: var(--primary-color, #5b73e8);
    font-size: 11px;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s;
    flex-shrink: 0;
}

.btn_download:hover:not(:disabled) {
    background: var(--primary-color, #5b73e8);
    color: #fff;
}

.btn_download:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Pagination */
.pagination_controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 4px;
    border-top: 1px solid var(--border-color);
}

.show_info span {
    font-size: 13px;
    color: var(--text-color-secondary);
}

.pagination_buttons {
    flex: 1;
    display: flex;
    justify-content: center;
}

.per_page_selector {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--text-color-secondary);
}

.per_page_selector select {
    padding: 5px 8px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background: var(--background-color);
    color: var(--text-color);
    cursor: pointer;
    font-size: 13px;
}
</style>
